import { API_BASE_URL } from '@/api/config'

/**
 * fetch-based Server-Sent-Events reader.
 *
 * Crossroads has no existing SSE pattern, and the browser-native `EventSource`
 * can't send an `Authorization` header. The gravitutor stream endpoint is a
 * bearer-authenticated POST/GET passthrough, so we open it with `fetch` and
 * parse the `data: <json>\n\n` wire frames ourselves.
 *
 * Ported from `supply_and_dispatch_aio` `useGraviStream` — but kept as a pure
 * util (no React, no store, no sessionStorage). The consuming hook is
 * responsible for store dispatch and refresh-recovery persistence.
 *
 * TODO(genapi): the gravitutor `/gravitutor/*` paths are not yet in the
 * generated OpenAPI types (`@/types/api/v1`). This util is typed generically
 * (`streamSSE<T>`); callers supply the frame shape. Switch the gravitutor
 * hooks to generated types after `yarn genapi` regenerates them.
 */

/** A parsed SSE `data:` frame. gravitutor emits `{ cursor, kind, data }`. */
export type SseFrame = {
  cursor: number
  kind: string
  data: Record<string, unknown>
}

export type StreamSSEOptions = {
  /** HTTP method for the stream request. gravitutor exposes the stream as GET. */
  method?: 'GET' | 'POST'
  /** JSON body to send (POST only). */
  body?: unknown
  /** Caller-controlled abort. Aborting ends the stream cleanly (no throw). */
  signal?: AbortSignal
  /**
   * Reconnect backoff schedule (ms). On a recoverable connection drop the
   * stream reconnects, appending `from_cursor=<last cursor seen>` so the
   * backend resumes where it left off. When the schedule is exhausted the
   * generator rethrows the last error.
   */
  reconnectBackoffMs?: number[]
  /** Override how the cursor is read off a frame (defaults to `frame.cursor`). */
  getCursor?: (frame: unknown) => number | undefined
}

const DEFAULT_BACKOFF = [1000, 2000, 5000, 10000]

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/** Read the bearer token the same place `typedClient` does. */
const getToken = (): string | null => {
  try {
    return globalThis.localStorage?.getItem('accessToken') ?? null
  } catch {
    return null
  }
}

const buildUrl = (path: string, fromCursor: number): string => {
  const base = API_BASE_URL.endsWith('/')
    ? API_BASE_URL.slice(0, -1)
    : API_BASE_URL
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  const sep = cleanPath.includes('?') ? '&' : '?'
  return `${base}${cleanPath}${sep}from_cursor=${fromCursor}`
}

/**
 * Open an SSE stream and yield each parsed `data:` JSON frame.
 *
 * @typeParam T - the parsed frame shape (defaults to {@link SseFrame}).
 */
export async function* streamSSE<T = SseFrame>(
  path: string,
  options: StreamSSEOptions = {},
): AsyncGenerator<T, void, unknown> {
  const {
    method = 'GET',
    body,
    signal,
    reconnectBackoffMs = DEFAULT_BACKOFF,
    getCursor = (frame: unknown) =>
      (frame as { cursor?: number } | null)?.cursor,
  } = options

  const token = getToken()
  let lastCursor = 0
  let attempts = 0

  while (true) {
    if (signal?.aborted) return

    let response: Response
    try {
      const headers = new Headers({ Accept: 'text/event-stream' })
      if (token) headers.set('Authorization', `Bearer ${token}`)
      if (method === 'POST' && body !== undefined) {
        headers.set('Content-Type', 'application/json')
      }

      response = await fetch(buildUrl(path, lastCursor), {
        method,
        headers,
        signal,
        body:
          method === 'POST' && body !== undefined
            ? JSON.stringify(body)
            : undefined,
      })

      if (!response.ok || response.body === null) {
        throw new Error(`SSE stream failed: ${response.status}`)
      }
    } catch (err) {
      // A caller-driven abort is a clean stop, not a failure.
      if (signal?.aborted || isAbortError(err)) return
      const delay = reconnectBackoffMs[attempts]
      if (delay === undefined) throw err
      await sleep(delay)
      attempts += 1
      continue
    }

    try {
      for await (const frame of parseFrames<T>(response.body)) {
        const cursor = getCursor(frame)
        if (typeof cursor === 'number') lastCursor = cursor
        yield frame
      }
      // Clean upstream close: the backend ended the stream because the task
      // reached a terminal state. This is "done" — do not reconnect.
      return
    } catch (err) {
      if (signal?.aborted || isAbortError(err)) return
      // Mid-stream drop (network blip, LB idle timeout): reconnect by cursor.
      const delay = reconnectBackoffMs[attempts]
      if (delay === undefined) throw err
      await sleep(delay)
      attempts += 1
    }
  }
}

const isAbortError = (err: unknown): boolean =>
  err instanceof Error && err.name === 'AbortError'

/** Decode a byte stream into parsed `data: <json>` SSE frames. */
async function* parseFrames<T>(
  stream: ReadableStream<Uint8Array>,
): AsyncGenerator<T, void, unknown> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buf = ''

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })

      // SSE events are delimited by a blank line.
      const frames = buf.split('\n\n')
      buf = frames.pop() ?? ''

      for (const raw of frames) {
        const parsed = parseFrame<T>(raw)
        if (parsed !== undefined) yield parsed
      }
    }

    // Flush any trailing frame that wasn't terminated by a blank line.
    const tail = (buf + decoder.decode()).trim()
    if (tail) {
      const parsed = parseFrame<T>(tail)
      if (parsed !== undefined) yield parsed
    }
  } finally {
    reader.releaseLock()
  }
}

/** Parse a single SSE frame, stripping the `data:` prefix from each line. */
function parseFrame<T>(raw: string): T | undefined {
  const data = raw
    .split('\n')
    .map((line) =>
      line.startsWith('data:') ? line.slice(5).trimStart() : line,
    )
    .join('\n')
    .trim()
  if (!data) return undefined
  try {
    return JSON.parse(data) as T
  } catch {
    // Ignore malformed / non-JSON frames (e.g. SSE comments / keepalives).
    return undefined
  }
}
